import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { SpecialistAvatar } from 'features/career-specialists/SpecialistAvatar';
import { SpecialistStatusBadge } from 'features/career-specialists/SpecialistStatusBadge';
import {
  isActiveSpecialist,
  normalizeSpecialist,
  normalizeSpecialistList,
} from 'features/career-specialists/specialists.helpers';
import { CONSULTATION_COMMUNICATION, operationsApi } from 'features/operations/operations.api';
import { datetimeLocalFromIso, isoFromDatetimeLocal, listItems, TIMEZONES } from 'features/operations/operations.helpers';
import { Button } from 'shared/components/Button';
import { Badge, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { formatDateTime, formatUserLabel, localizedName, safeHttpUrl } from 'shared/utilities/format';

function ConsultationSpecialistSummary({ specialist, locale, t }) {
  if (!specialist) {
    return '—';
  }
  const normalized = normalizeSpecialist(specialist) || specialist;
  const name = localizedName(normalized, locale) || normalized.name_en || normalized.name_ar || normalized.name || '—';
  const title = locale === 'ar'
    ? normalized.title_ar || normalized.title
    : normalized.title_en || normalized.title;

  return (
    <div className="avatar-row consultation-specialist-summary">
      <SpecialistAvatar specialist={normalized} />
      <div>
        <div>{name}</div>
        {title ? <div className="muted">{title}</div> : null}
        <div className="muted">
          {(normalized.languages || []).join(', ') || '—'}
          {normalized.timezone ? ` · ${normalized.timezone}` : ''}
        </div>
        <SpecialistStatusBadge isActive={normalized.is_active} />
      </div>
    </div>
  );
}

export function ConsultationDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.consultationsManage);
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [specialistId, setSpecialistId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [timezone, setTimezone] = useState('Asia/Riyadh');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [method, setMethod] = useState('video');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [conflict, setConflict] = useState(null);
  const [pendingAction, setPendingAction] = useState('');

  const query = useQuery({
    queryKey: queryKeys.consultation(id),
    queryFn: ({ signal }) => operationsApi.showConsultation(id, { signal }),
  });
  const consultation = query.data || {};
  const historyQuery = useQuery({
    queryKey: queryKeys.consultationHistory(id),
    queryFn: ({ signal }) => operationsApi.consultationHistory(id, { signal }),
  });
  const specialistsQuery = useQuery({
    queryKey: queryKeys.specialists({ is_active: true }),
    enabled: canManage && query.isSuccess && consultation.status === 'pending_review',
    queryFn: ({ signal }) => operationsApi.listSpecialists({ is_active: true }, { signal }),
    select: (data) => normalizeSpecialistList(data).filter(isActiveSpecialist),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.consultation(id) });
    queryClient.invalidateQueries({ queryKey: queryKeys.consultationHistory(id) });
    queryClient.invalidateQueries({ queryKey: ['operations', 'consultations'] });
  };

  const run = useMutation({
    mutationFn: ({ action, payload }) => {
      if (action === 'assign') return operationsApi.assignConsultation(id, payload.specialist_id);
      if (action === 'schedule') return operationsApi.scheduleConsultation(id, payload);
      if (action === 'complete') return operationsApi.completeConsultation(id);
      if (action === 'reject') return operationsApi.rejectConsultation(id, payload.reason);
      return operationsApi.cancelConsultation(id, payload.reason);
    },
    onSuccess: () => {
      setConflict(null);
      setErrors({});
      refresh();
      toast.success(t('status.saved'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) setErrors(mapFieldErrors(error.errors));
      if (error instanceof ApiError && error.isConflict()) {
        setConflict(error.message);
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
    onSettled: () => setPendingAction(''),
  });

  if (query.isLoading) return <Skeleton rows={8} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  const meeting = safeHttpUrl(consultation.meeting_url);
  const history = listItems(historyQuery.data?.status_history || historyQuery.data);
  const reportVisible = Object.prototype.hasOwnProperty.call(consultation, 'report') && Boolean(consultation.report);
  const assignableSpecialists = specialistsQuery.data || [];
  const busy = run.isPending;

  return (
    <div className="stack-form">
      <PageHeader title={`${t('consultations.detail')} ${consultation.reference || `#${consultation.id}`}`} />
      {conflict ? (
        <p className="warning-banner" role="alert">
          {conflict} {t('conflict.keepLocal')}
          <button type="button" className="btn btn-ghost" onClick={() => query.refetch()}>{t('conflict.refresh')}</button>
        </p>
      ) : null}
      <p><strong>{t('consultations.status')}:</strong> <Badge>{t(`consultations.statuses.${consultation.status}`)}</Badge></p>
      <p><strong>{t('consultations.user')}:</strong> {formatUserLabel(consultation.user, t)}</p>
      <p><strong>{t('consultations.goal')}:</strong> {consultation.goal ? t(`consultations.goals.${consultation.goal}`) : '—'}</p>
      <p><strong>{t('consultations.question')}:</strong> {consultation.question || '—'}</p>
      <p><strong>{t('consultations.preferredPeriod')}:</strong> {consultation.preferred_period ? t(`consultations.periods.${consultation.preferred_period}`) : '—'}</p>
      <p>
        <strong>{t('consultations.attempt')}:</strong>{' '}
        {consultation.attempt_id ? <Link to={paths.attemptDetail(consultation.attempt_id)}>#{consultation.attempt_id}</Link> : '—'}
      </p>
      <p>
        <strong>{t('consultations.consent')}:</strong>{' '}
        {consultation.consent_withdrawn_at
          ? t('consultations.consentWithdrawn')
          : consultation.share_report_consent
            ? t('consultations.consentGranted')
            : t('consultations.consentDenied')}
      </p>
      <div>
        <strong>{t('consultations.specialist')}:</strong>
        <ConsultationSpecialistSummary specialist={consultation.specialist} locale={locale} t={t} />
      </div>
      <p><strong>{t('consultations.scheduledAt')}:</strong> {formatDateTime(consultation.scheduled_at, locale)} {consultation.display_timezone || ''}</p>
      <p><strong>{t('consultations.communication')}:</strong> {consultation.communication_method ? t(`consultations.communicationMethods.${consultation.communication_method}`) : '—'}</p>
      <p>
        <strong>{t('consultations.meetingUrl')}:</strong>{' '}
        {meeting ? <a href={meeting} target="_blank" rel="noopener noreferrer">{meeting}</a> : '—'}
      </p>
      {consultation.rejection_reason ? <p><strong>{t('consultations.reject')}:</strong> {consultation.rejection_reason}</p> : null}
      {consultation.cancellation_reason ? <p><strong>{t('consultations.cancel')}:</strong> {consultation.cancellation_reason}</p> : null}

      <section>
        <h2>{t('consultations.report')}</h2>
        {reportVisible
          ? <pre className="preview-block">{JSON.stringify(consultation.report, null, 2)}</pre>
          : <p className="muted">{t('consultations.reportHidden')}</p>}
      </section>
      <section>
        <h2>{t('consultations.history')}</h2>
        <ol className="timeline">
          {history.map((event, index) => (
            <li key={`${event.created_at}-${index}`}>
              {event.from_status || '—'} → {event.to_status || '—'} · {formatDateTime(event.created_at, locale)}
              {event.reason ? ` · ${event.reason}` : ''}
            </li>
          ))}
        </ol>
      </section>

      {canManage && consultation.status === 'pending_review' ? (
        <form
          className="card stack-form"
          onSubmit={(event) => {
            event.preventDefault();
            setPendingAction('assign');
            run.mutate({ action: 'assign', payload: { specialist_id: Number(specialistId) } });
          }}
        >
          <h2>{t('consultations.assign')}</h2>
          <select className="input" value={specialistId} onChange={(event) => setSpecialistId(event.target.value)} required aria-label={t('consultations.specialist')}>
            <option value="">{t('consultations.assign')}</option>
            {assignableSpecialists.map((item) => {
              const name = localizedName(item, locale) || item.name_en || item.name_ar || item.name;
              const title = locale === 'ar' ? item.title_ar || item.title : item.title_en || item.title;
              return (
                <option key={item.id} value={item.id}>
                  {name}{title ? ` — ${title}` : ''}{(item.languages || []).length ? ` (${item.languages.join(', ')})` : ''}{item.timezone ? ` · ${item.timezone}` : ''}
                </option>
              );
            })}
          </select>
          {errors.specialist_id?.[0] ? <span className="field-error" role="alert">{errors.specialist_id[0]}</span> : null}
          <Button type="submit" loading={busy && pendingAction === 'assign'} disabled={busy}>{t('actions.assign')}</Button>
        </form>
      ) : null}

      {canManage && consultation.status === 'matched' ? (
        <form
          className="card stack-form"
          onSubmit={(event) => {
            event.preventDefault();
            setPendingAction('schedule');
            run.mutate({
              action: 'schedule',
              payload: {
                scheduled_at: isoFromDatetimeLocal(scheduledAt),
                display_timezone: timezone,
                meeting_url: meetingUrl || null,
                communication_method: method,
              },
            });
          }}
        >
          <h2>{t('consultations.schedule')}</h2>
          <p className="muted">{t('consultations.noReschedule')}</p>
          <label className="field">
            <span className="field-label">{t('consultations.scheduledAt')}</span>
            <input className="input" type="datetime-local" value={scheduledAt || datetimeLocalFromIso(consultation.scheduled_at)} onChange={(event) => setScheduledAt(event.target.value)} required />
          </label>
          <label className="field">
            <span className="field-label">{t('consultations.timezone')}</span>
            <select className="input" value={timezone} onChange={(event) => setTimezone(event.target.value)}>
              {TIMEZONES.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('consultations.meetingUrl')}</span>
            <input className="input" value={meetingUrl} onChange={(event) => setMeetingUrl(event.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">{t('consultations.communication')}</span>
            <select className="input" value={method} onChange={(event) => setMethod(event.target.value)}>
              {CONSULTATION_COMMUNICATION.map((item) => (
                <option key={item} value={item}>{t(`consultations.communicationMethods.${item}`)}</option>
              ))}
            </select>
          </label>
          <Button type="submit" loading={busy && pendingAction === 'schedule'} disabled={busy}>{t('actions.schedule')}</Button>
        </form>
      ) : null}

      {canManage && consultation.status === 'scheduled' ? (
        <Button
          loading={busy && pendingAction === 'complete'}
          disabled={busy}
          onClick={async () => {
            if (await confirm({ body: t('consultations.complete') })) {
              setPendingAction('complete');
              run.mutate({ action: 'complete', payload: {} });
            }
          }}
        >
          {t('actions.complete')}
        </Button>
      ) : null}

      {canManage && ['pending_review', 'matched'].includes(consultation.status) ? (
        <form
          className="card stack-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!(await confirm({ body: t('consultations.reject') }))) return;
            setPendingAction('reject');
            run.mutate({ action: 'reject', payload: { reason } });
          }}
        >
          <h2>{t('consultations.reject')}</h2>
          <textarea className="input" value={reason} onChange={(event) => setReason(event.target.value)} required />
          <Button type="submit" tone="danger" loading={busy && pendingAction === 'reject'} disabled={busy}>{t('actions.reject')}</Button>
        </form>
      ) : null}

      {canManage && ['pending_review', 'matched', 'scheduled'].includes(consultation.status) ? (
        <form
          className="card stack-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!(await confirm({ body: t('consultations.cancel') }))) return;
            setPendingAction('cancel');
            run.mutate({ action: 'cancel', payload: { reason } });
          }}
        >
          <h2>{t('consultations.cancel')}</h2>
          <textarea className="input" value={reason} onChange={(event) => setReason(event.target.value)} required />
          <Button type="submit" tone="danger" loading={busy && pendingAction === 'cancel'} disabled={busy}>{t('consultations.cancel')}</Button>
        </form>
      ) : null}
    </div>
  );
}
